import { ReactNode } from 'react';
import { ActivityKind } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { Badge } from '@Components/Badge';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { LabelPosition } from '@Components/ToggleSwitch/ToggleSwitch';
import { Design, Size } from '@Components/designConstants';
import { useActivityFeedFilterContext } from '../useActivityFeedData';
import { useOracleEventCounts } from '../useOracleEventCounts';
import { KIND_TOGGLE_ROWS } from './activity-feed-filters-constants';
import { FilterSection } from './FilterSection';

// "Show in feed" toggle row. kinds is inclusionary — matches the URL semantics of the other
// filters — but the visual affordance is a per-kind toggle. Only Event has a designed card
// today; turning Meetings or Tasks on surfaces the placeholder path (which logs + returns null).
export const KindTogglesSection = (): ReactNode => {
  const { filterState, setFilterState } = useActivityFeedFilterContext();
  const counts = useOracleEventCounts();
  const { kinds } = filterState;

  const toggleKind = (kind: ActivityKind) => {
    const next = kinds.includes(kind) ? kinds.filter(k => k !== kind) : [...kinds, kind];
    setFilterState(prev => ({ ...prev, kinds: next }));
  };

  return (
    <FilterSection label='Show in feed'>
      {KIND_TOGGLE_ROWS.map(({ value, label }) => (
        <KindToggleRow
          key={value}
          label={label}
          count={counts?.byKind[value] ?? 0}
          checked={kinds.includes(value)}
          onChange={() => toggleKind(value)}
        />
      ))}
    </FilterSection>
  );
};

interface KindToggleRowProps {
  label: string;
  count: number;
  checked: boolean;
  onChange: () => void;
}

const KindToggleRow = ({ label, count, checked, onChange }: KindToggleRowProps): ReactNode => (
  <div className='activity-feed-kind-toggle-row'>
    <ToggleSwitch
      label={label}
      labelPosition={LabelPosition.TRAILING}
      checked={checked}
      onChange={onChange}
      size={Size.SM}
    />
    <Badge size={Size.SM} design={Design.NEUTRAL}>
      {count}
    </Badge>
  </div>
);
